import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check, ChevronRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Button } from '@mdxui/primitives'
import { cn } from '../../../lib/cn'
import { Markdown } from '../../../lib/markdown'
import { CodeWindowHero } from '../../code-window'

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

const headerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
}

/**
 * Props for FeaturesShowcaseIcons component
 *
 * @remarks
 * **CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Each feature MUST have an `icon` prop (React element)
 * - Icons should be from react-icons (Lu* icons recommended)
 * - Supports 1-5 feature steps
 * - Each step supports image or code window display
 * - Best for step-by-step workflows with distinct icons per step
 */
export interface FeaturesShowcaseIconsProps {
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Array of feature steps with icons (supports 1-5 steps) */
  features?: Array<{
    /** Icon element to display (required) */
    icon: ReactNode
    title: string
    description: string
    /** Bullet points - shown when button is not provided */
    bullets?: string[]
    /** Secondary button - replaces bullets when provided */
    button?: { text: string; url: string }
    /** Optional ghost "Learn more" link below bullets or buttons */
    learnMore?: { text: string; url: string }
    image?: { src: string; alt: string }
    codeWindow?: { code: string; language: string; filename?: string }
  }>
  /** Toggle between image and code window display */
  mediaType?: 'image' | 'code'
  className?: string
}

/**
 * FeaturesShowcaseIcons - Feature showcase cards with icons instead of numbers
 *
 * Similar to FeaturesShowcase but uses custom icons instead of numbered indicators.
 * Each step displays an icon in a rounded badge, making it ideal for
 * showcasing workflows where each step has a distinct visual identity.
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────────┐
 * │  [Icon]                      ┌──────────┐  │
 * │  Title                       │  Image   │  │
 * │  Description                 │    or    │  │
 * │  - Bullet 1                  │   Code   │  │
 * │  - Bullet 2                  └──────────┘  │
 * │  [Button] [Learn more]                     │
 * └────────────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Step-by-step tutorials with distinct actions
 * - Feature showcases where icons convey meaning
 * - Installation/setup guides with visual indicators
 *
 * @example
 * ```tsx
 * import { Download, Code, Rocket } from 'lucide-react'
 *
 * <FeaturesShowcaseIcons
 *   heading="Get Started"
 *   features={[
 *     { icon: <Download />, title: 'Install', description: '...' },
 *     { icon: <Code />, title: 'Configure', description: '...' },
 *     { icon: <Rocket />, title: 'Deploy', description: '...' },
 *   ]}
 * />
 * ```
 */
const FeaturesShowcaseIcons = ({
  features = [],
  heading = 'Built for developers',
  description: sectionDescription = 'Everything you need to build production-ready applications.',
  mediaType = 'image',
  className,
}: FeaturesShowcaseIconsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const cardAnimation = prefersReducedMotion ? reducedMotionVariants : cardVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {(heading || sectionDescription) && (
          <motion.div
            variants={headerAnimation}
            className="mx-auto mb-16 max-w-3xl text-center"
          >
            {heading && (
              <h2 className="text-3xl font-semibold lg:text-4xl">
                {heading}
              </h2>
            )}
            {sectionDescription && (
              <div className="mt-4">
                <Markdown size="lg">{sectionDescription}</Markdown>
              </div>
            )}
          </motion.div>
        )}
        <div className="space-y-10">
          {features.map((feature) => (
            <motion.div key={feature.title} variants={cardAnimation}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                bullets={feature.bullets}
                button={feature.button}
                learnMore={feature.learnMore}
                image={feature.image}
                codeWindow={feature.codeWindow}
                mediaType={mediaType}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}

interface FeatureCardProps {
  icon: ReactNode
  title: string
  description: string
  bullets?: string[]
  button?: { text: string; url: string }
  learnMore?: { text: string; url: string }
  image?: { src: string; alt: string }
  codeWindow?: { code: string; language: string; filename?: string }
  mediaType?: 'image' | 'code'
}

function FeatureCard({
  icon,
  title,
  description,
  bullets,
  button,
  learnMore,
  image = { src: '/placeholders/placeholder-1.svg', alt: 'Feature image' },
  codeWindow,
  mediaType = 'image',
}: FeatureCardProps) {
  return (
    <div className="grid overflow-hidden rounded-[var(--radius)] border border-border md:grid-cols-2 shadow-sm bg-muted-foreground/5">
      <div className="flex h-full min-h-[300px] flex-col justify-between p-6 md:min-h-[450px] lg:p-8 xl:p-10">
        <div>
          <span className="mb-4 flex size-10 items-center justify-center rounded-[var(--radius-sm,calc(var(--radius)*0.5))] bg-accent text-foreground">
            <span className="[&>svg]:size-5">{icon}</span>
          </span>
          <h3 className="mb-3 text-2xl font-semibold sm:mb-5 md:text-3xl">
            {title}
          </h3>
          <div className="prose prose-sm max-w-none text-muted-foreground md:prose-base prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none">
            <ReactMarkdown>{description}</ReactMarkdown>
          </div>
        </div>
        <div className="mt-6 space-y-8">
          {/* Bullets list */}
          {bullets && bullets.length > 0 && (
            <ul className="space-y-3">
              {bullets.map((bullet) => (
                <li key={bullet} className="flex gap-x-3">
                  <Check className="mt-0.5 size-4 shrink-0 sm:mt-1" />
                  <span className="text-sm md:text-base [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-foreground">
                    <ReactMarkdown>{bullet}</ReactMarkdown>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Optional buttons below bullets */}
          {(button || learnMore) && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              {button && (
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <a href={button.url}>{button.text}</a>
                </Button>
              )}
              {learnMore && (
                <a
                  href={learnMore.url}
                  className="group inline-flex w-full items-center justify-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:w-auto sm:justify-start"
                >
                  {learnMore.text}
                  <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="relative order-first overflow-hidden max-h-80 md:order-last md:max-h-[500px] flex items-center justify-center">
        {mediaType === 'code' && codeWindow ? (
          <CodeWindowHero
            code={codeWindow.code}
            language={codeWindow.language}
            showHeader={false}
            showCopy={false}
            theme="dark"
            style="borderless"
            size="full"
            className="h-full w-full p-0 sm:p-4"
          />
        ) : (
          <img
            src={image.src}
            alt={image.alt}
            className="h-full w-full object-cover"
          />
        )}
      </div>
    </div>
  )
}

export { FeaturesShowcaseIcons }
